import React, { lazy, useContext, useEffect } from "react";
import { BrowserRouter as Router, Navigate, useRoutes } from "react-router-dom";
import { DndProvider } from "react-dnd";
import { MultiBackend } from "react-dnd-multi-backend";
import { HTML5toTouch } from "rdndmb-html5-to-touch"; // or any other pipeline

import { AuthProvider, AuthContext } from "./context/AuthContext";
import TranslationProvider from "./context/Internationalization";
import { SnackBarProvider } from "./context/SnackBarContext";
import InvitationProvider from "./context/InvitationContext";
import SuperAdminProvider from "./context/SuperAdminContext";
import { GoogleMapsProvider } from "./context/GoogleMapsContext";
import { AppProvider } from "./context/AppContext";
import { BillingGateProvider } from "./context/BillingGateContext";
import { isClubAdmin, isSuperAdmin } from "./utility/Utils";
import { checkAppVersion } from "./auth/utils";
import {
  ACCOUNT_SETTINGS_ROUTE,
  BILLING_SPLAT_ROUTE,
  CLUB_SPLAT_ROUTE,
  FILES_SPLAT_ROUTE,
  FORMS_SPLAT_ROUTE,
  LOGIN_ROUTE,
  REGISTRATION_ROUTE,
  MANAGER_HOME_ROUTE,
  PLAYERS_SPLAT_ROUTE,
  ROOT_ROUTE,
  SHOP_SPLAT_ROUTE,
  STAFF_ROUTE,
  SPLAT_PATH,
  SUPER_ADMIN_CLUBS_ROUTE,
  SUPER_ADMIN_IMPERSONATE_ROUTE,
  SUPER_ADMIN_ROLES_ROUTE,
} from "./routes";

import AppShell from "./AppShell";
import FirebaseActionLinkHandler from "./Components/FirebaseActionLinkHandler";
import Club from "./pages/Club/Club";
import BillingProtectedRoute from "./pages/Billing/BillingProtectedRoute";
import Login from "./pages/Login/Login";
import Registration from "./pages/Registration/Registration";
import SessionBoundary from "./Components/SessionBoundary";
import DeploymentUpdateNotice from "./Components/DeploymentUpdateNotice";
import SuperAdminClubs from "./pages/SuperAdmin/SuperAdminClubs";
import SuperAdminImpersonate from "./pages/SuperAdmin/SuperAdminImpersonate";
import SuperAdminRoles from "./pages/SuperAdmin/SuperAdminRoles";

const Home = lazy(() => import("./pages/Home/Home"));
const Membership = lazy(() => import("./pages/Membership/Membership"));
const Files = lazy(() => import("./pages/Files/Files"));
const Shop = lazy(() => import("./pages/Shop/Shop"));
const Staff = lazy(() => import("./pages/Staff/Staff"));
const Billing = lazy(() => import("./pages/Billing/Billing"));
const Forms = lazy(() => import("./pages/Forms/Forms"));
const AccountSettings = lazy(() =>
  import("./pages/AccountSettings/AccountSettings")
);

function getLoginPathWithMagicLinkParams() {
  const searchParams = new URLSearchParams(window.location.search);
  const isMagicLink =
    ["signIn", "verifyAndChangeEmail"].includes(searchParams.get("mode")) &&
    searchParams.get("oobCode");

  return isMagicLink ? `${LOGIN_ROUTE}${window.location.search}` : LOGIN_ROUTE;
}

function App() {
  const auth = useContext(AuthContext);

  const user = auth.authState.user;

  const isUserSuperAdmin = isSuperAdmin(user);
  const isUserClubAdmin = isClubAdmin(user);

  let routes = [
    { path: REGISTRATION_ROUTE, element: <Registration /> },
    {
      path: LOGIN_ROUTE,
      element: <Login />,
    },
    {
      path: ROOT_ROUTE,
      element:
        auth.isAuthenticated() && (isUserClubAdmin || isUserSuperAdmin) ? (
          <BillingProtectedRoute user={user}>
            <Navigate to={MANAGER_HOME_ROUTE} />
          </BillingProtectedRoute>
        ) : (
          <Navigate to={getLoginPathWithMagicLinkParams()} />
        ),
    },
    {
      path: SPLAT_PATH,
      element:
        auth.isAuthenticated() && (isUserClubAdmin || isUserSuperAdmin) ? (
          <BillingProtectedRoute user={user}>
            <AppShell>
              <Home />
            </AppShell>
          </BillingProtectedRoute>
        ) : (
          <Navigate to={getLoginPathWithMagicLinkParams()} />
        ),
    },
    {
      path: CLUB_SPLAT_ROUTE,
      element:
        auth.isAuthenticated() && (isUserClubAdmin || isUserSuperAdmin) ? (
          <BillingProtectedRoute user={user}>
            <AppShell>
              <Club />
            </AppShell>
          </BillingProtectedRoute>
        ) : (
          <Navigate to={getLoginPathWithMagicLinkParams()} />
        ),
    },
    {
      path: PLAYERS_SPLAT_ROUTE,
      element: auth.isAuthenticated() ? (
        <BillingProtectedRoute user={user}>
          <AppShell>
            <Membership />
          </AppShell>
        </BillingProtectedRoute>
      ) : (
        <Navigate to={getLoginPathWithMagicLinkParams()} />
      ),
    },
    {
      path: FILES_SPLAT_ROUTE,
      element:
        auth.isAuthenticated() && (isUserClubAdmin || isUserSuperAdmin) ? (
          <BillingProtectedRoute user={user}>
            <AppShell>
              <Files />
            </AppShell>
          </BillingProtectedRoute>
        ) : (
          <Navigate to={getLoginPathWithMagicLinkParams()} />
        ),
    },
    {
      path: SHOP_SPLAT_ROUTE,
      element:
        auth.isAuthenticated() && (isUserClubAdmin || isUserSuperAdmin) ? (
          <BillingProtectedRoute user={user}>
            <AppShell>
              <Shop />
            </AppShell>
          </BillingProtectedRoute>
        ) : (
          <Navigate to={getLoginPathWithMagicLinkParams()} />
        ),
    },
    {
      path: STAFF_ROUTE,
      element:
        auth.isAuthenticated() && (isUserClubAdmin || isUserSuperAdmin) ? (
          <BillingProtectedRoute user={user}>
            <AppShell>
              <Staff />
            </AppShell>
          </BillingProtectedRoute>
        ) : (
          <Navigate to={getLoginPathWithMagicLinkParams()} />
        ),
    },
    {
      path: FORMS_SPLAT_ROUTE,
      element:
        auth.isAuthenticated() && (isUserClubAdmin || isUserSuperAdmin) ? (
          <BillingProtectedRoute user={user}>
            <AppShell>
              <Forms />
            </AppShell>
          </BillingProtectedRoute>
        ) : (
          <Navigate to={getLoginPathWithMagicLinkParams()} />
        ),
    },
    {
      path: BILLING_SPLAT_ROUTE,
      element:
        auth.isAuthenticated() && (isUserClubAdmin || isUserSuperAdmin) ? (
          <BillingProtectedRoute user={user}>
            <AppShell>
              <Billing />
            </AppShell>
          </BillingProtectedRoute>
        ) : (
          <Navigate to={getLoginPathWithMagicLinkParams()} />
        ),
    },
    {
      path: ACCOUNT_SETTINGS_ROUTE,
      element:
        auth.isAuthenticated() && (isUserClubAdmin || isUserSuperAdmin) ? (
          <BillingProtectedRoute user={user}>
            <AppShell>
              <AccountSettings />
            </AppShell>
          </BillingProtectedRoute>
        ) : (
          <Navigate to={getLoginPathWithMagicLinkParams()} />
        ),
    },
    {
      path: SUPER_ADMIN_CLUBS_ROUTE,
      element:
        auth.isAuthenticated() && isUserSuperAdmin ? (
          <AppShell>
            <SuperAdminClubs />
          </AppShell>
        ) : (
          <Navigate to={getLoginPathWithMagicLinkParams()} />
        ),
    },
    {
      path: SUPER_ADMIN_IMPERSONATE_ROUTE,
      element:
        auth.isAuthenticated() && isUserSuperAdmin ? (
          <AppShell>
            <SuperAdminImpersonate />
          </AppShell>
        ) : (
          <Navigate to={getLoginPathWithMagicLinkParams()} />
        ),
    },
    {
      path: SUPER_ADMIN_ROLES_ROUTE,
      element:
        auth.isAuthenticated() && isUserSuperAdmin ? (
          <AppShell>
            <SuperAdminRoles />
          </AppShell>
        ) : (
          <Navigate to={getLoginPathWithMagicLinkParams()} />
        ),
    },
    {
      path: "*",
      element: <Navigate to={ROOT_ROUTE} />,
    },
  ];
  if (!isUserSuperAdmin) {
    routes = routes
      .filter((route) => route.path !== SUPER_ADMIN_CLUBS_ROUTE)
      .filter((route) => route.path !== SUPER_ADMIN_IMPERSONATE_ROUTE)
      .filter((route) => route.path !== SUPER_ADMIN_ROLES_ROUTE);
  }

  return (
    <SuperAdminProvider isUserSuperAdmin={isUserSuperAdmin}>
      <FirebaseActionLinkHandler>
        <SessionBoundary>
          <DeploymentUpdateNotice />
          {useRoutes(routes)}
        </SessionBoundary>
      </FirebaseActionLinkHandler>
    </SuperAdminProvider>
  );
}

const AppWrapper = () => {
  useEffect(() => {
    checkAppVersion();
  }, []);

  return (
    <div>
      <Router
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <div className="Container">
          <AuthProvider>
            <BillingGateProvider>
              <AppProvider>
                <TranslationProvider>
                  <SnackBarProvider>
                    <InvitationProvider>
                      <DndProvider
                        backend={MultiBackend}
                        options={HTML5toTouch}
                      >
                        <GoogleMapsProvider>
                          <App />
                        </GoogleMapsProvider>
                      </DndProvider>
                    </InvitationProvider>
                  </SnackBarProvider>
                </TranslationProvider>
              </AppProvider>
            </BillingGateProvider>
          </AuthProvider>
        </div>
      </Router>
    </div>
  );
};

export default AppWrapper;
